<template>
    
  <b-card class="col" border-variant="warning">
    <LineChartGenerator 
      :chart-options="chartOptions" 
      :chart-data="chartData" 
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey" 
      :plugins="plugins" 
      :css-classes="cssClasses" 
      :styles="styles"
      :width="width" 
      :height="height" 
    />
  </b-card>
</template>

<script>
import { Line as LineChartGenerator } from 'vue-chartjs/legacy'


import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement
} from 'chart.js'

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement
)

export default {
  name: "ChartSignosVitales",

  components: {
    LineChartGenerator,
  },

  props: {
    historialSignos: Array,

    chartId: {
      type: String,
      default: 'line-chart'
    },

    datasetIdKey: {
      type: String,
      default: 'label'
    },

    width: {
      type: Number,
      default: 500
    },

    height: {
      type: Number,
      default: 500
    },

    cssClasses: {
      default: '',
      type: String
    },

    styles: {
      type: Object,
      default: () => ({})
    },

    plugins: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      chartData: {
        labels: [],
        datasets: []
      },

      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'left',  // Esto posiciona la leyenda a la izquierda
            labels: {
              usePointStyle: true,  // Usa puntos para representar los colores de la leyenda
              padding: 25,
              border: 'gray'
            }
          },
        },
        scales: {
          x: {
            position: 'top',
            grid: {
              color: 'gray',
            },
          },

          'y-rc': {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'Frecuencia Cardíaca (bpm)'
            },
            min: 0,
            max: 240,
            grid: {
              color: 'gray',
              borderColor: 'gray',   // Color del borde
              drawOnChartArea: true
            },
            ticks: {
              stepSize: 20,
              callback: (value) => `${value}`
            }
          },

          'y-temp': {
            type: 'linear',
            position: 'left',
            offset: true,
            title: {
              display: true,
              text: 'Temperatura (°C)',
            },
            min: 35,
            max: 50,
            grid: {
              borderColor: 'gray',
              drawOnChartArea: false,
            },
            ticks: {
              stepSize: 1,
              callback: (value) => `${value}`
            },

          },

          'y-pv': {
            type: 'linear',
            position: 'left',
            offset: true,
            title: {
              display: true,
              text: 'PV',
            },
            min: 1,
            max: 17,
            grid: {
              borderColor: 'gray',  
              drawOnChartArea: false,
            },
            ticks: {
              stepSize: 2,
              callback: (value) => `${value}`
            },


          }
        }
      }
    }
  },

  created() {
    this.fetchData()
  },

  methods: {
    generarHorasCada15Minutos(inicio = "00:00", cantidad = 10) {
      const [h, m] = inicio.split(":").map(Number)
      let minutos = h * 60 + m
      const resultado = []
      for (let i = 0; i < cantidad; i++) {
        const horas = String(Math.floor(minutos / 60)).padStart(2, '0')
        const mins = String(minutos % 60).padStart(2, '0')
        resultado.push(`${horas}:${mins}`)
        minutos += 15
      }
      return resultado
    },

    fetchData() {
      const horas = this.generarHorasCada15Minutos("00:00", 12)

      const induccion = [
        { hora: "00:00", temperatura: 237 },
        { hora: "00:30", temperatura: 237 }
      ]

      const cirugia = [
        { hora: "00:00", temperatura: 220 },
        { hora: "00:30", temperatura: 220 }
      ]

      const dataTemperaturas = [
        { hora: "00:00", temperatura: 37.2 },
        { hora: "00:15", temperatura: 38.2 },
        { hora: "00:30", temperatura: 37 }
      ]

      const frecuenciaCardiaca = [
        { hora: "00:00", temperatura: 70 },
        { hora: "00:15", temperatura: 80 },
        { hora: "00:30", temperatura: 76 }
      ]

      const respiracion = [
        { hora: "00:00", temperatura: 18 },
        { hora: "00:15", temperatura: 20 },
        { hora: "00:30", temperatura: 22 }
      ]

      const dataInd  = horas.map(h => induccion.find(d => d.hora === h)?.temperatura ?? null )
      const dataCir  = horas.map(h => cirugia.find(d => d.hora === h)?.temperatura ?? null )
      const dataTemp = horas.map(h => dataTemperaturas.find(d => d.hora === h)?.temperatura ?? null )
      const dataFC   = horas.map(h => frecuenciaCardiaca.find(d => d.hora === h)?.temperatura ?? null )
      const dataResp = horas.map(h => respiracion.find(d => d.hora === h)?.temperatura ?? null )

      this.chartData = {
        labels: horas,
        datasets: [
          {
            label: 'Inducción',
            data: dataInd,
            borderColor: 'blue',
            backgroundColor: 'blue',
            pointRadius: 8,
            pointHoverRadius: 12,
            pointBorderWidth: 2,
            pointBackgroundColor: 'blue',
            yAxisID: 'y-rc',
            fill: true,
            spanGaps: true
          },
          {
            label: 'Cirugía',
            data: dataCir,
            borderColor: 'yellow',
            backgroundColor: 'yellow',
            pointRadius: 8,
            pointHoverRadius: 12,
            pointBorderWidth: 2,
            pointBackgroundColor: 'yellow',
            yAxisID: 'y-rc',
            fill: true,
            spanGaps: true
          },
          {
            label: 'Temperatura (°C)',
            data: dataTemp,
            borderColor: '#ff6384',
            backgroundColor: '#ff6384',
            pointStyle: 'triangle',
            pointRadius: 10,
            pointHoverRadius: 12,
            pointBackgroundColor: 'transparent',
            pointBorderColor: 'rgba(255, 99, 132, 1)',
            pointBorderWidth: 2,
            yAxisID: 'y-temp',
            fill: true,
            spanGaps: true
          },
          {
            label: 'Frecuencia Cardíaca (bpm)',
            data: dataFC,
            borderColor: '#36A2EB',
            backgroundColor: '#36A2EB',
            pointRadius: 8,
            pointHoverRadius: 12,
            pointBorderWidth: 2,
            pointBackgroundColor: '#36A2EB',
            yAxisID: 'y-rc',
            fill: true,
            spanGaps: true
          },
          {
            label: 'Respiración',
            data: dataResp,
            borderColor: '#4caf50',
            backgroundColor: '#4caf50',
            pointRadius: 8,
            pointHoverRadius: 12,
            pointBorderWidth: 2,
            pointBackgroundColor: 'transparent',
            yAxisID: 'y-rc',
            fill: true,
            spanGaps: true
          }
        ]
      }
    }
  }
}
</script>
